// Pre-join lobby (F-LOB-1, design "Waiting room"): permission CTA first
// (§6.2 — preview before grant is impossible), then self-view preview with
// round mic/cam toggles, level meter, device pickers under the stage, and the
// join panel on the right. Graceful paths: no camera → mic-only; denied →
// listen-only; stuck prompt → join without devices.

import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import logoUrl from '../assets/brizina-logo.svg';
import { engine } from '../engine';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { LabeledInput, Select } from '../ui/Field';
import { IconButton } from '../ui/IconButton';
import {
  IconCamera,
  IconCameraOff,
  IconMicOff,
  IconMicrophone,
} from '../ui/icons';

export interface PreJoinChoices {
  micEnabled: boolean;
  camEnabled: boolean;
  name: string;
  /** Model B passphrase typed in the lobby (§5.2), when the link lacks #k=. */
  passphrase?: string;
}

interface Props {
  meetingTitle: string | null;
  /** Meeting code shown under the title (design: "Product sync · xkq-…"). */
  slug?: string;
  /** Prefilled display name for signed-in users; guests type theirs (§6.2). */
  initialName: string;
  /** Signed-in users may leave the name as-is; guests must provide one. */
  nameRequired: boolean;
  /** Guests type their name; signed-in users join under their IdP name
   * (design: no name field on the signed-in waiting room). */
  nameEditable?: boolean;
  /** Model B meeting whose link carried no key — ask for the passphrase. */
  needPassphrase?: boolean;
  joining: boolean;
  /** Label of the join action: "Join now" (host) or "Ask to join" (knockers). */
  joinLabel: string;
  /** Display names already in the live session (lobby presence) + total. */
  attendees?: string[];
  attendeeCount?: number;
  onJoin: (choices: PreJoinChoices) => void;
  /** Back out of the lobby (design "Cancel"). */
  onCancel: () => void;
}

// 'audio-only': no usable camera (missing hardware or camera-only block) but
// the microphone works — never drop the mic just because the camera failed.
type PermissionState = 'ask' | 'granted' | 'audio-only' | 'denied';

const KINDS: { kind: MediaDeviceKind; label: string }[] = [
  { kind: 'videoinput', label: 'Camera' },
  { kind: 'audioinput', label: 'Microphone' },
  { kind: 'audiooutput', label: 'Speaker' },
];

export function PreJoin({
  meetingTitle,
  slug,
  initialName,
  nameRequired,
  nameEditable = true,
  needPassphrase = false,
  joining,
  joinLabel,
  attendees = [],
  attendeeCount = 0,
  onJoin,
  onCancel,
}: Props) {
  const [permission, setPermission] = useState<PermissionState>('ask');
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [name, setName] = useState(initialName);
  const [passphrase, setPassphrase] = useState('');
  const [devices, setDevices] = useState<Record<string, MediaDeviceInfo[]>>({});
  const videoRef = useRef<HTMLVideoElement>(null);
  const meterRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const requestPermissions = useCallback(() => {
    void (async () => {
      try {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        setPermission('granted');
      } catch {
        // Camera missing/blocked — retry with microphone alone.
        try {
          streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
          setPermission('audio-only');
        } catch {
          setPermission('denied');
        }
      }
    })();
  }, []);

  // Wire the preview + a lightweight level meter once granted; enumerate
  // devices for the pickers (labels are available post-grant).
  useEffect(() => {
    if (permission !== 'granted' && permission !== 'audio-only') return;
    const stream = streamRef.current;
    if (!stream) return;
    if (videoRef.current) videoRef.current.srcObject = stream;

    void (async () => {
      const byKind: Record<string, MediaDeviceInfo[]> = {};
      for (const { kind } of KINDS) byKind[kind] = await engine.devices.list(kind);
      setDevices(byKind);
    })();

    const audioCtx = new AudioContext();
    const src = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    src.connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;
    const draw = () => {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128) / 128);
      if (meterRef.current) meterRef.current.style.transform = `scaleY(${Math.min(1, peak * 2)})`;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      void audioCtx.close();
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [permission]);

  // Device pickers: persist the choice (bz-device-prefs) and re-acquire the
  // preview with the newly selected hardware (output devices only persist).
  const onPickDevice = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const kind = e.currentTarget.dataset.kind as MediaDeviceKind;
    const deviceId = e.currentTarget.value;
    engine.devices.savePref(kind, deviceId);
    if (kind === 'audiooutput') return;
    void (async () => {
      const stream = streamRef.current;
      if (!stream) return;
      try {
        const constraints: MediaStreamConstraints =
          kind === 'videoinput'
            ? { video: { deviceId: { exact: deviceId } } }
            : { audio: { deviceId: { exact: deviceId } } };
        const fresh = await navigator.mediaDevices.getUserMedia(constraints);
        const [newTrack] = kind === 'videoinput' ? fresh.getVideoTracks() : fresh.getAudioTracks();
        const old = kind === 'videoinput' ? stream.getVideoTracks() : stream.getAudioTracks();
        old.forEach((t) => {
          t.stop();
          stream.removeTrack(t);
        });
        if (newTrack) stream.addTrack(newTrack);
        if (videoRef.current && kind === 'videoinput') videoRef.current.srcObject = stream;
      } catch {
        /* keep the previous device */
      }
    })();
  }, []);

  const toggleCamPreview = useCallback(() => {
    setCamEnabled((v) => {
      streamRef.current?.getVideoTracks().forEach((t) => (t.enabled = !v));
      return !v;
    });
  }, []);
  const toggleMicPreview = useCallback(() => {
    setMicEnabled((v) => {
      streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = !v));
      return !v;
    });
  }, []);
  const onNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setName(e.target.value),
    [],
  );
  const onPassphrase = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setPassphrase(e.target.value),
    [],
  );

  const join = useCallback(() => {
    // Stop preview tracks before connecting so the engine can claim devices.
    streamRef.current?.getTracks().forEach((t) => t.stop());
    const hasAudio = permission === 'granted' || permission === 'audio-only';
    onJoin({
      micEnabled: micEnabled && hasAudio,
      camEnabled: camEnabled && permission === 'granted',
      name: name.trim(),
      passphrase: passphrase.trim() || undefined,
    });
  }, [onJoin, micEnabled, camEnabled, permission, name, passphrase]);

  // Receive-only escape hatch: works even while a permission prompt is
  // pending/stuck; devices can still be enabled from the in-call controls.
  const joinWithoutDevices = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    onJoin({
      micEnabled: false,
      camEnabled: false,
      name: name.trim(),
      passphrase: passphrase.trim() || undefined,
    });
  }, [onJoin, name, passphrase]);

  const nameMissing = nameRequired && name.trim() === '';
  const presence = presenceLine(attendees, attendeeCount);
  const passphraseMissing = needPassphrase && passphrase.trim() === '';
  const blocked = nameMissing || passphraseMissing;
  const showVideo = permission === 'granted' && camEnabled;

  return (
    <div className="bz-lobby">
      <header className="bz-lobby-header">
        <img src={logoUrl} alt="Brizina" className="bz-logo" />
      </header>
      <div className="bz-lobby-main">
        <div className="bz-prejoin-stage">
          <div className="bz-prejoin-preview">
            {permission === 'granted' && (
              <video
                ref={videoRef}
                className="bz-prejoin-video bz-tile-mirror"
                autoPlay
                playsInline
                muted
                hidden={!camEnabled}
              />
            )}
            {!showVideo && <Avatar name={name || 'You'} identity="self" isSelf size={110} />}
            {permission !== 'ask' && !showVideo && (
              <span className="bz-prejoin-camoff">
                {permission === 'granted' ? 'Camera is off' : 'No camera'}
              </span>
            )}
            {name.trim() !== '' && <span className="bz-prejoin-name-tag">{name.trim()}</span>}
            {permission === 'ask' && (
              <div className="bz-prejoin-controls">
                <Button variant="primary" pill onClick={requestPermissions}>
                  Enable camera &amp; microphone
                </Button>
              </div>
            )}
            {(permission === 'granted' || permission === 'audio-only') && (
              <>
                <div className="bz-meter-track">
                  <div ref={meterRef} className="bz-meter-fill" />
                </div>
                <div className="bz-prejoin-controls">
                  <IconButton
                    label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
                    tone={micEnabled ? 'default' : 'danger'}
                    onClick={toggleMicPreview}
                  >
                    {micEnabled ? <IconMicrophone size={19} /> : <IconMicOff size={19} />}
                  </IconButton>
                  {permission === 'granted' && (
                    <IconButton
                      label={camEnabled ? 'Turn camera off' : 'Turn camera on'}
                      tone={camEnabled ? 'default' : 'danger'}
                      onClick={toggleCamPreview}
                    >
                      {camEnabled ? <IconCamera size={19} /> : <IconCameraOff size={19} />}
                    </IconButton>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="bz-prejoin-devices">
            {KINDS.map(({ kind, label }) => {
              const list = devices[kind] ?? [];
              if (list.length === 0 || (kind === 'videoinput' && permission !== 'granted'))
                return null;
              return (
                <Select key={kind} data-kind={kind} onChange={onPickDevice} defaultValue="" aria-label={label}>
                  <option value="" disabled>
                    {label}: default
                  </option>
                  {list.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `${label} ${d.deviceId.slice(0, 6)}`}
                    </option>
                  ))}
                </Select>
              );
            })}
          </div>
        </div>

        <div className="bz-prejoin-side">
          <h2 className="bz-prejoin-title">Ready to join?</h2>
          <p className="bz-prejoin-meta">
            {meetingTitle ?? 'Meeting'}
            {slug ? (
              <>
                {' · '}
                <code>{slug}</code>
              </>
            ) : null}
          </p>

          {attendees.length > 0 && (
            <div className="bz-prejoin-presence">
              <div className="bz-avatar-stack">
                {attendees.slice(0, 3).map((n, i) => (
                  <Avatar key={i} name={n} identity={n} size={30} />
                ))}
              </div>
              <p className="bz-prejoin-presence-line">{presence}</p>
            </div>
          )}

          {nameEditable && (
            <LabeledInput
              label="Your name"
              value={name}
              onChange={onNameChange}
              placeholder="Shown to other attendees"
              maxLength={64}
              required={nameRequired}
            />
          )}

          {needPassphrase && (
            <>
              <LabeledInput
                label="Meeting passphrase"
                type="password"
                value={passphrase}
                onChange={onPassphrase}
                placeholder="Shared with you by the organizer"
                maxLength={256}
              />
              <p className="bz-prejoin-hint">
                This meeting is zero-knowledge — the passphrase never reaches our servers.
              </p>
            </>
          )}

          {permission === 'denied' && (
            <p className="bz-prejoin-denied">
              Camera and microphone are blocked. You can join listen/view-only, and re-enable
              access from your browser&rsquo;s site settings to publish later.
            </p>
          )}
          {permission === 'audio-only' && (
            <p className="bz-prejoin-denied">
              No usable camera was found (is the laptop lid closed, or camera access off in system
              settings?). Your microphone works — you can join with audio.
            </p>
          )}

          {permission !== 'ask' && (
            <Button variant="primary" pill block onClick={join} disabled={joining || blocked}>
              {joining
                ? 'Joining…'
                : permission === 'denied'
                  ? `${joinLabel} (listen only)`
                  : permission === 'audio-only'
                    ? `${joinLabel} (microphone only)`
                    : joinLabel}
            </Button>
          )}
          {permission === 'ask' && (
            <Button pill block onClick={joinWithoutDevices} disabled={joining || blocked}>
              Join without camera and microphone
            </Button>
          )}
          <Button pill block className="bz-prejoin-cancel" onClick={onCancel}>
            Cancel
          </Button>
          {nameMissing && <p className="bz-prejoin-hint">Enter your name to continue.</p>}
        </div>
      </div>
    </div>
  );
}

/** "Maya, Jonas and 3 others are in this call" (design copy). */
function presenceLine(attendees: string[], total: number): string {
  const first = attendees.map((n) => n.split(/\s+/)[0] ?? n);
  if (total <= 0 || first.length === 0) return '';
  if (total === 1) return `${first[0]} is in this call`;
  if (total === 2) return `${first[0]} and ${first[1] ?? 'one other'} are in this call`;
  return `${first[0]}, ${first[1]} and ${total - 2} ${total - 2 === 1 ? 'other' : 'others'} are in this call`;
}
